import React, { useEffect, useRef, useState } from 'react';
import '../../styles/components/guide/GuideIntroduction.css';
import { FaTimes, FaImage, FaEdit } from 'react-icons/fa';
import RichTextToolbar from './RichTextToolbar';
import { getDisplayLength } from '../common/TextValidator';

const GuideIntroduction = ({
  introduction,
  introductionImage,
  introductionImagePreview,
  introductionImageDescription,
  showIntroImageDescInput,
  errors,
  maxIntroductionLength,
  handleIntroductionChange,
  handleIntroductionImageChange,
  handleRemoveIntroductionImage,
  setShowIntroImageDescInput,
  setIntroductionImageDescription,
  setError,
  debouncedValidateIntroduction,
  debouncedValidateImageDesc,
  updateImageDescError,
  isAdmin,
}) => {
  const contentEditableRef = useRef(null);
  const [lastValidIntro, setLastValidIntro] = useState(introduction);
  useEffect(() => {
    if (contentEditableRef.current && contentEditableRef.current.innerHTML !== introduction) {
      contentEditableRef.current.innerHTML = introduction;
      setLastValidIntro(introduction);
    }
  }, [introduction]);
  const handleContentInput = (e) => {
    const html = e.currentTarget.innerHTML;
    if (getDisplayLength(html) === 0) {
      setLastValidIntro('');
      setError(null);
      handleIntroductionChange({ target: { value: '' } });
      debouncedValidateIntroduction('');
      return;
    }
    if (getDisplayLength(html) <= maxIntroductionLength) {
      setLastValidIntro(html);
      setError(null);
      debouncedValidateIntroduction(html);
      handleIntroductionChange({ target: { value: html } });
    } else {
      e.currentTarget.innerHTML = lastValidIntro;
      const sel = window.getSelection();
      if (sel.rangeCount) {
        sel.collapse(e.currentTarget, 1);
      }
    }
  };
  const handleImageDescChange = (e) => {
    const val = e.target.value;
    setIntroductionImageDescription(val);
    if(val.trim() === '') {
      updateImageDescError('');
    } else {
      debouncedValidateImageDesc(val, updateImageDescError);
    }
  };
  return (
    <section className="guide-introduction-section">
      <div className="form-group">
        <label htmlFor="guide-introduction">Introduction <span className="required">*</span></label>
        <RichTextToolbar
          editorRef={contentEditableRef}
          textValue={introduction}
          onChange={(val) => {
            if (getDisplayLength(val) === 0) {
              setLastValidIntro('');
              setError(null);
              handleIntroductionChange({ target: { value: '' } });
              debouncedValidateIntroduction('');
              return;
            }
            if (getDisplayLength(val) <= maxIntroductionLength) {
              setLastValidIntro(val);
              handleIntroductionChange({ target: { value: val } });
              debouncedValidateIntroduction(val);
            }
          }}
          maxLength={maxIntroductionLength}
          isAdmin={isAdmin}
        />
        <textarea
          id="guide-introduction"
          value={introduction}
          maxLength={maxIntroductionLength}
          onChange={handleIntroductionChange}
          placeholder="Write an introduction..."
          style={{ display: 'none' }}
        />
        <div
          ref={contentEditableRef}
          className="content-editable-intro"
          contentEditable
          data-placeholder="Write an introduction..."
          onInput={handleContentInput}
          role="textbox"
          aria-multiline="true"
        />
        <div className="guide-edit-character-count">
          {getDisplayLength(introduction)}/{maxIntroductionLength} characters
        </div>
        {errors.introduction && <span className="error-text">{errors.introduction}</span>}
      </div>
      <div className="form-group">
        <div className="upload-button-container">
          <button type="button" className="upload-image-button" onClick={() => document.getElementById('introduction-image').click()} aria-label="Upload introduction image">
            <FaImage className="save-icon" /> {introductionImage || introductionImagePreview ? 'Change Image' : 'Upload Image'}
          </button>
          {(introductionImage || introductionImagePreview) && (
            <button type="button" className="upload-image-button" onClick={() => setShowIntroImageDescInput(!showIntroImageDescInput)} aria-label="Toggle introduction image description input">
              <FaEdit className="save-icon" /> {introductionImageDescription ? 'Edit Description' : 'Add Description'}
            </button>
          )}
        </div>
        <input
          type="file"
          id="introduction-image"
          accept="image/png, image/jpeg, image/jpg, image/gif"
          style={{ display: 'none' }}
          onChange={handleIntroductionImageChange}
          aria-label="Introduction image file upload"
        />
        {errors.introductionImage && <span className="error-text">{errors.introductionImage}</span>}
        {introductionImagePreview && (
          <div className="day-image-preview">
            <img src={introductionImagePreview} alt="Introduction Preview" className="day-image" />
            <button type="button" className="remove-image-button" onClick={handleRemoveIntroductionImage} aria-label="Remove introduction image">
              <FaTimes />
            </button>
          </div>
        )}
        {showIntroImageDescInput ? (
          <div>
            <input
              type="text"
              maxLength={60}
              value={introductionImageDescription}
              onChange={handleImageDescChange}
              placeholder="Enter image description..."
              className="image-description-input"
              aria-label="Introduction image description input"
            />
            <div className="guide-edit-character-count">
              {introductionImageDescription.length}/60 characters
            </div>
            {errors.introductionImageDescription && <span className="error-text">{errors.introductionImageDescription}</span>}
          </div>
        ) : (
          errors.introductionImageDescription && <span className="error-text">{errors.introductionImageDescription}</span>
        )}
      </div>
    </section>
  );
};
export default React.memo(GuideIntroduction);
